import API_BASE_URL from '../config/apiConfig';
import { apiClient } from './authService';

/**
 * List agents with pagination + optional search.
 * Endpoint: GET /api/agents?page=0&size=20&search=
 * @param {Object} params
 * @param {number} params.page
 * @param {number} params.size
 * @param {string} params.search
 * @returns {Promise<Object>}
 */
export const getAgents = async ({ page = 0, size = 20, search = '' } = {}) => {
  try {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
      search: (search || '').toString(),
    });

    const response = await apiClient(
      `${API_BASE_URL}/api/agents?${queryParams.toString()}`,
      { method: 'GET' },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to fetch agents';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get agents error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * List agents assigned to the current landlord (my agents).
 * Endpoint: GET /api/agents/my-agents
 *           GET /api/agents/my-agents?propertyId={id}  ← scoped to one property
 * @param {Object} [params]
 * @param {string} [params.propertyId]  - Optional: filter to a specific property
 * @returns {Promise<Object>}
 */
export const getMyAgents = async ({ propertyId } = {}) => {
  try {
    const url = propertyId
      ? `${API_BASE_URL}/api/agents/my-agents?propertyId=${encodeURIComponent(propertyId)}`
      : `${API_BASE_URL}/api/agents/my-agents`;
    const response = await apiClient(
      url,
      { method: 'GET' },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to fetch my agents';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get my agents error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * List landlords linked to the current agent (my landlords).
 * Endpoint: GET /api/agent/landlords
 * @returns {Promise<Object>}
 */
export const getMyLandlords = async () => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/agent/landlords`,
      { method: 'GET' },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to fetch my landlords';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get my landlords error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

